import { useEffect, useRef } from "react";

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
export const isTurnstileEnabled = Boolean(TURNSTILE_SITE_KEY);

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const el = document.createElement("script");
      el.src = SCRIPT_SRC;
      el.async = true;
      el.defer = true;
      el.onload = () => resolve(window.turnstile);
      el.onerror = () => {
        scriptPromise = null;
        reject(new Error("Could not load the verification challenge"));
      };
      document.head.appendChild(el);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget. Renders nothing when REACT_APP_TURNSTILE_SITE_KEY is unset.
 * Bump `resetSignal` to force a fresh challenge (tokens are single-use).
 */
export default function TurnstileWidget({ onToken, onError, resetSignal = 0, theme = "dark" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!isTurnstileEnabled) return undefined;
    let cancelled = false;
    loadTurnstileScript()
      .then(turnstile => {
        if (cancelled || !containerRef.current || widgetIdRef.current != null) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme,
          callback: token => onTokenRef.current?.(token),
          "expired-callback": () => onTokenRef.current?.(null),
          "error-callback": () => {
            onTokenRef.current?.(null);
            onErrorRef.current?.();
          },
        });
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current?.();
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [theme]);

  useEffect(() => {
    if (resetSignal === 0) return;
    if (widgetIdRef.current != null && window.turnstile) {
      onTokenRef.current?.(null);
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [resetSignal]);

  if (!isTurnstileEnabled) return null;
  return <div ref={containerRef} style={{ display: "flex", justifyContent: "center", minHeight: 65 }} />;
}
